import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, ExternalLink, RefreshCw, X } from "lucide-react";
import { api } from "../lib/api";

const RFC_ENTITY_TYPES = new Set(["rfc_revenue_report", "rfc_premium_video_page"]);
const DATE_RANGES = [["today", "Today"], ["7d", "7d"], ["30d", "30d"], ["90d", "90d"], ["all", "All"]];

function safeDate(value) {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime()) ? date : null;
}

function getReportAmount(capture) {
  if (capture.entity_type !== "rfc_revenue_report") return null;
  const rawValue = capture.rfc_payload?.totals?.allTimeRevenue;
  if (rawValue === null || rawValue === undefined || rawValue === "") return null;
  const value = Number(rawValue);
  return Number.isFinite(value) ? value : null;
}

export function normalizeRfcCaptures(snapshots = [], syncRuns = []) {
  const runsById = new Map(syncRuns.map((run) => [run.sync_run_id, run]));
  return snapshots
    .filter((snapshot) => snapshot.platform === "rawfuckclub" && RFC_ENTITY_TYPES.has(snapshot.entity_type))
    .map((snapshot) => {
      const run = runsById.get(snapshot.sync_run_id);
      const payload = snapshot.rfc_payload || {};
      const entityWarnings = Array.isArray(snapshot.field_validation_warnings) ? snapshot.field_validation_warnings : [];
      const payloadWarnings = Array.isArray(payload.validationWarnings) ? payload.validationWarnings : [];
      const warnings = entityWarnings.length ? entityWarnings : payloadWarnings;
      return {
        ...snapshot,
        capture_id: snapshot.sync_run_id || snapshot.snapshot_id || `${snapshot.account_id}:${snapshot.entity_type}:${snapshot.captured_at}`,
        account: snapshot.account_id || snapshot.connected_account_id || snapshot.creator_username || "Unknown account",
        capture_type: snapshot.page_type || snapshot.entity_type,
        capture_source: snapshot.capture_source || "browser_extension",
        status: run?.status || "unknown",
        sync_errors: run?.errors || [],
        validation_warnings: warnings,
        notes: Array.isArray(warnings) ? warnings.join("; ") : String(warnings || ""),
        source_url: snapshot.source_url || payload.sourceUrl || "",
        normalized_values: snapshot.normalized_payload || snapshot.normalized || {},
        rfc_payload: payload,
        earnings: getReportAmount({ ...snapshot, rfc_payload: payload }),
        currency: payload.currency || snapshot.currency || (snapshot.entity_type === "rfc_revenue_report" ? "USD" : ""),
      };
    })
    .sort((left, right) => (safeDate(right.captured_at)?.getTime() || 0) - (safeDate(left.captured_at)?.getTime() || 0));
}

export function summarizeRfcCaptures(captures = [], now = new Date()) {
  const latestRevenueByAccount = new Map();
  for (const capture of captures) {
    if (capture.entity_type !== "rfc_revenue_report") continue;
    const existing = latestRevenueByAccount.get(capture.account);
    if (!existing || (safeDate(capture.captured_at)?.getTime() || 0) > (safeDate(existing.captured_at)?.getTime() || 0)) {
      latestRevenueByAccount.set(capture.account, capture);
    }
  }
  const latestCapture = captures.reduce((latest, capture) => {
    const timestamp = safeDate(capture.captured_at);
    return timestamp && (!latest || timestamp > latest) ? timestamp : latest;
  }, null);
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const lastSevenDays = now.getTime() - 7 * 24 * 60 * 60 * 1000;
  const countSince = (start) => captures.filter((capture) => {
    const date = safeDate(capture.captured_at);
    return date && date.getTime() >= start && date.getTime() <= now.getTime();
  }).length;
  const distinctAccounts = new Set(captures.map((capture) => `${capture.platform}:${capture.account}`));
  return {
    totalEarnings: Array.from(latestRevenueByAccount.values()).reduce((sum, capture) => sum + (capture.earnings ?? 0), 0),
    captureCount: captures.length,
    latestCaptureAt: latestCapture?.toISOString() || null,
    distinctAccounts: distinctAccounts.size,
    capturesToday: countSince(todayStart.getTime()),
    capturesLast7Days: countSince(lastSevenDays),
  };
}

function dateRangeStart(range, now) {
  if (range === "all") return null;
  if (range === "today") {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return start;
  }
  const start = new Date(now);
  start.setDate(start.getDate() - Number.parseInt(range, 10));
  return start;
}

export function filterRfcCaptures(captures = [], filters = {}, now = new Date()) {
  const start = dateRangeStart(filters.dateRange || "all", now);
  const search = String(filters.search || "").trim().toLowerCase();
  return captures.filter((capture) => {
    const date = safeDate(capture.captured_at);
    if (start && (!date || date < start || date > now)) return false;
    if (filters.account && capture.account !== filters.account) return false;
    if (filters.captureType && capture.capture_type !== filters.captureType) return false;
    if (filters.status && capture.status !== filters.status) return false;
    if (!search) return true;
    const searchable = [capture.account, capture.notes, capture.source_url, capture.capture_id]
      .filter(Boolean).join(" ").toLowerCase();
    return searchable.includes(search);
  });
}

function formatDateTime(value) {
  const date = safeDate(value);
  return date ? date.toLocaleString() : "—";
}

function formatMoney(value, currency = "USD") {
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 2 }).format(value);
}

function Stat({ label, value, detail }) {
  return <div className="min-w-0 border px-3 py-3 sm:px-4" style={{ background: "var(--gio-surface)", borderColor: "var(--gio-border)" }}>
    <p className="text-xs" style={{ color: "var(--gio-muted-2)" }}>{label}</p>
    <p className="mt-1 truncate text-lg font-semibold tabular-nums sm:text-xl" style={{ color: "var(--gio-text)" }}>{value}</p>
    {detail && <p className="mt-1 truncate text-[11px]" style={{ color: "var(--gio-muted-2)" }}>{detail}</p>}
  </div>;
}

const controlClass = "min-h-10 w-full min-w-0 border px-3 py-2 text-sm outline-none focus:ring-1";
const controlStyle = { background: "var(--gio-surface)", borderColor: "var(--gio-border)", color: "var(--gio-text)" };

export default function RFCExtensionDashboard() {
  const [snapshots, setSnapshots] = useState([]);
  const [syncRuns, setSyncRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selectedCapture, setSelectedCapture] = useState(null);
  const [filters, setFilters] = useState({ dateRange: "all", account: "", captureType: "", status: "", search: "" });

  const loadCaptures = useCallback(async () => {
    setRefreshing(true);
    try {
      const [snapshotResponse, runResponse] = await Promise.all([
        api.get("/api/platform-data/entity-snapshots", { params: { platform: "rawfuckclub", limit: 500 } }),
        api.get("/api/platform-data/sync-runs", { params: { platform: "rawfuckclub", limit: 500 } }),
      ]);
      setSnapshots(snapshotResponse.data?.snapshots || []);
      setSyncRuns(runResponse.data?.sync_runs || []);
      setError("");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Extension captures are unavailable.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadCaptures(); }, [loadCaptures]);
  useEffect(() => {
    if (!selectedCapture) return undefined;
    const closeOnEscape = (event) => { if (event.key === "Escape") setSelectedCapture(null); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedCapture]);

  const captures = useMemo(() => normalizeRfcCaptures(snapshots, syncRuns), [snapshots, syncRuns]);
  const summary = useMemo(() => summarizeRfcCaptures(captures), [captures]);
  const visibleCaptures = useMemo(() => filterRfcCaptures(captures, filters), [captures, filters]);
  const accounts = Array.from(new Set(captures.map((capture) => capture.account))).sort();
  const captureTypes = Array.from(new Set(captures.map((capture) => capture.capture_type))).sort();
  const statuses = Array.from(new Set(captures.map((capture) => capture.status))).sort();
  const updateFilter = (key) => (event) => setFilters((current) => ({ ...current, [key]: event.target.value }));

  return <section className="space-y-5" aria-label="RFC extension captures">
    <div className="flex flex-wrap items-end justify-between gap-3 border-b pb-4" style={{ borderColor: "var(--gio-border)" }}>
      <div>
        <h2 className="text-sm font-semibold" style={{ color: "var(--gio-text)" }}>Browser extension captures</h2>
        <p className="mt-1 text-xs" style={{ color: "var(--gio-muted-2)" }}>RawFuckClub data captured from paired creator pages. Manual revenue entries are not included.</p>
      </div>
      <button type="button" onClick={loadCaptures} disabled={refreshing} className="inline-flex min-h-10 items-center gap-2 border px-3 text-sm disabled:opacity-60" style={{ borderColor: "var(--gio-border)", color: "var(--gio-text)" }}>
        <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />Refresh
      </button>
    </div>

    {error && <p role="alert" className="border px-3 py-2 text-sm" style={{ borderColor: "#9f334f", color: "#ff9db8" }}>{error}</p>}

    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
      <Stat label="Total captured earnings" value={formatMoney(summary.totalEarnings)} detail="Latest report per account" />
      <Stat label="Number of captures" value={summary.captureCount.toLocaleString()} />
      <Stat label="Latest capture" value={formatDateTime(summary.latestCaptureAt)} />
      <Stat label="Accounts / platforms" value={summary.distinctAccounts.toLocaleString()} />
      <Stat label="Captures today" value={summary.capturesToday.toLocaleString()} />
      <Stat label="Captures last 7 days" value={summary.capturesLast7Days.toLocaleString()} />
    </div>

    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
      <label className="text-xs" style={{ color: "var(--gio-muted-2)" }}>Date range
        <select aria-label="Date range" value={filters.dateRange} onChange={updateFilter("dateRange")} className={controlClass} style={controlStyle}>{DATE_RANGES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
      </label>
      <label className="text-xs" style={{ color: "var(--gio-muted-2)" }}>Account / platform
        <select aria-label="Account / platform" value={filters.account} onChange={updateFilter("account")} className={controlClass} style={controlStyle}><option value="">All accounts</option>{accounts.map((account) => <option key={account} value={account}>{account}</option>)}</select>
      </label>
      <label className="text-xs" style={{ color: "var(--gio-muted-2)" }}>Capture type / source
        <select aria-label="Capture type / source" value={filters.captureType} onChange={updateFilter("captureType")} className={controlClass} style={controlStyle}><option value="">All capture types</option>{captureTypes.map((type) => <option key={type} value={type}>{type}</option>)}</select>
      </label>
      <label className="text-xs" style={{ color: "var(--gio-muted-2)" }}>Sync status
        <select aria-label="Sync status" value={filters.status} onChange={updateFilter("status")} className={controlClass} style={controlStyle}><option value="">All statuses</option>{statuses.map((status) => <option key={status} value={status}>{status}</option>)}</select>
      </label>
      <label className="text-xs" style={{ color: "var(--gio-muted-2)" }}>Search captures
        <input aria-label="Search captures" value={filters.search} onChange={updateFilter("search")} placeholder="Account, note, source URL, capture ID" className={controlClass} style={controlStyle} />
      </label>
    </div>

    <div className="overflow-x-auto border" style={{ borderColor: "var(--gio-border)" }}>
      <table className="w-full min-w-[1180px] text-left text-sm">
        <thead style={{ background: "var(--gio-surface-2)", color: "var(--gio-muted-2)" }}><tr>
          {["Date/time", "Account/platform", "Earnings/amount", "Currency", "Capture type/source", "Source URL", "Status", "Notes", "Actions"].map((label) => <th key={label} className="whitespace-nowrap px-3 py-2 text-xs font-medium">{label}</th>)}
        </tr></thead>
        <tbody>
          {visibleCaptures.map((capture) => <tr key={capture.capture_id} className="border-t align-top" style={{ borderColor: "var(--gio-border)", color: "var(--gio-text)" }}>
            <td className="whitespace-nowrap px-3 py-3 text-xs">{formatDateTime(capture.captured_at)}</td>
            <td className="px-3 py-3"><span className="block">{capture.account}</span><span className="text-xs" style={{ color: "var(--gio-muted-2)" }}>RawFuckClub</span></td>
            <td className="whitespace-nowrap px-3 py-3 tabular-nums">{capture.earnings === null ? "—" : formatMoney(capture.earnings, capture.currency || "USD")}</td>
            <td className="px-3 py-3">{capture.currency || "—"}</td>
            <td className="px-3 py-3"><span>{capture.capture_type}</span><span className="mt-1 block text-xs" style={{ color: "var(--gio-muted-2)" }}>{capture.capture_source.replace(/_/g, " ")}</span></td>
            <td className="max-w-[200px] px-3 py-3">{capture.source_url ? <a href={capture.source_url} target="_blank" rel="noreferrer" className="inline-flex max-w-full items-center gap-1 truncate" style={{ color: "var(--gio-primary)" }} title={capture.source_url}>{capture.source_url}<ExternalLink size={12} className="shrink-0" /></a> : "—"}</td>
            <td className="px-3 py-3"><span className="inline-block border px-2 py-0.5 text-xs" style={{ borderColor: capture.status === "completed" ? "var(--gio-primary)" : "var(--gio-border)", color: capture.status === "failed" ? "#ff9db8" : "var(--gio-text)" }}>{capture.status}</span></td>
            <td className="max-w-[220px] px-3 py-3 text-xs" style={{ color: "var(--gio-muted-2)" }}>{capture.notes || "—"}</td>
            <td className="px-3 py-3"><button type="button" title="View details" aria-label={`View details for ${capture.capture_id}`} onClick={() => setSelectedCapture(capture)} className="inline-flex min-h-8 min-w-8 items-center justify-center border" style={{ borderColor: "var(--gio-border)", color: "var(--gio-text)" }}><Eye size={15} /></button></td>
          </tr>)}
          {!visibleCaptures.length && <tr><td colSpan={9} className="px-4 py-10 text-center" style={{ color: "var(--gio-muted-2)" }}>
            {loading ? "Loading extension captures…" : <><p>No extension captures found for this filter.</p><p className="mt-2 text-xs">Open a supported RawFuckClub page while signed in, then open the Creator Sync extension and choose “Sync this page now.”</p></>}
          </td></tr>}
        </tbody>
      </table>
    </div>

    {selectedCapture && <div className="fixed inset-0 z-[100] flex justify-end bg-black/70" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedCapture(null); }}>
      <aside role="dialog" aria-modal="true" aria-label="Capture details" className="h-full w-full max-w-2xl overflow-y-auto border-l p-4 sm:p-6" style={{ background: "var(--gio-surface)", borderColor: "var(--gio-border)" }}>
        <div className="mb-5 flex items-start justify-between gap-3 border-b pb-4" style={{ borderColor: "var(--gio-border)" }}>
          <div className="min-w-0"><h2 className="text-base font-semibold" style={{ color: "var(--gio-text)" }}>Capture details</h2><p className="mt-1 break-all text-xs" style={{ color: "var(--gio-muted-2)" }}>{selectedCapture.capture_id}</p></div>
          <button type="button" aria-label="Close details" onClick={() => setSelectedCapture(null)} className="inline-flex min-h-9 min-w-9 items-center justify-center border" style={{ borderColor: "var(--gio-border)", color: "var(--gio-text)" }}><X size={16} /></button>
        </div>
        <dl className="grid grid-cols-1 gap-x-5 gap-y-3 text-sm sm:grid-cols-2">
          <Detail label="Capture timestamp" value={formatDateTime(selectedCapture.captured_at)} />
          <Detail label="Account/platform" value={`${selectedCapture.account} · RawFuckClub`} />
          <Detail label="Capture type" value={selectedCapture.capture_type} />
          <Detail label="Status" value={selectedCapture.status} />
          <Detail label="Adapter version" value={selectedCapture.adapter_version || "Unavailable"} />
          <Detail label="Extension version" value={selectedCapture.extension_version || "Unavailable"} />
          <div className="sm:col-span-2"><dt className="text-xs" style={{ color: "var(--gio-muted-2)" }}>Source URL</dt>{selectedCapture.source_url ? <a href={selectedCapture.source_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex break-all text-sm" style={{ color: "var(--gio-primary)" }}>{selectedCapture.source_url}</a> : <dd>Unavailable</dd>}</div>
        </dl>
        <PayloadBlock title="Parsed / normalized values" value={selectedCapture.normalized_values} />
        <PayloadBlock title="Full raw extension payload" value={selectedCapture.rfc_payload} />
        <PayloadBlock title="Validation warnings" value={selectedCapture.validation_warnings} />
        <PayloadBlock title="Sync errors" value={selectedCapture.sync_errors} />
      </aside>
    </div>}
  </section>;
}

function Detail({ label, value }) {
  return <div><dt className="text-xs" style={{ color: "var(--gio-muted-2)" }}>{label}</dt><dd className="mt-1 break-words" style={{ color: "var(--gio-text)" }}>{value || "Unavailable"}</dd></div>;
}

function PayloadBlock({ title, value }) {
  return <section className="mt-5"><h3 className="mb-2 text-sm font-semibold" style={{ color: "var(--gio-text)" }}>{title}</h3><pre className="max-h-72 overflow-auto border p-3 text-xs" style={{ background: "var(--gio-surface-2)", borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}>{JSON.stringify(value ?? null, null, 2)}</pre></section>;
}